import { useRef } from 'react'

interface PrintLabelProps {
  buttonText?: string
  buttonType?: 'primary' | 'default' | 'text'
  children: React.ReactNode
  onBeforePrint?: () => void
}

export default function PrintLabel({ buttonText = '打印', buttonType = 'default', children, onBeforePrint }: PrintLabelProps) {
  const contentRef = useRef<HTMLDivElement>(null)

  const handlePrint = () => {
    onBeforePrint?.()
    window.print()
  }

  return (
    <>
      <button
        onClick={handlePrint}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '4px 12px', borderRadius: 4,
          border: '1px solid #d9d9d9',
          background: buttonType === 'primary' ? '#55266c' : '#fff',
          color: buttonType === 'primary' ? '#fff' : '#55266c',
          cursor: 'pointer', fontSize: 14, marginLeft: 8
        }}
      >
        🖨️ {buttonText}
      </button>
      <div ref={contentRef} style={{ display: 'none' }} className="print-target">
        {children}
      </div>
    </>
  )
}

/** 包装标签打印组件 */
interface PackagingLabelProps {
  plan: {
    plan_no: string
    product?: { name: string; code: string; unit: string }
    output_qty?: string | number
    actual_qty?: string | number
    output_unit?: string
    planned_date?: string
    end_time?: string
    warehouse?: { name: string }
    bom?: { name: string; items?: Array<{ material?: { name: string; code: string }; quantity: string | number; unit: string }> }
  }
  /** 保质期天数（天），自动计算到期日 */
  shelfLifeDays?: number
}

export function PackagingLabel({ plan, shelfLifeDays = 7 }: PackagingLabelProps) {
  const now = new Date()
  const produceDate = plan.end_time ? new Date(plan.end_time) : now
  const expireDate = new Date(produceDate)
  expireDate.setDate(expireDate.getDate() + shelfLifeDays)

  const formatDate = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`

  const qty = parseFloat(String(plan.actual_qty || plan.output_qty || 0))
  const unit = plan.output_unit || plan.product?.unit || 'kg'
  const batchNo = plan.plan_no
  const productName = plan.product?.name || ''
  const warehouse = plan.warehouse?.name || ''

  return (
    <div style={{ width: 80, fontFamily: 'Microsoft YaHei, SimHei, monospace' }}>
      <div style={{ textAlign: 'center', fontSize: 14, fontWeight: 'bold', borderBottom: '1px solid #000', paddingBottom: 4, marginBottom: 6 }}>
        智厨云 · 包装标签
      </div>
      <div style={{ fontSize: 11 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', borderBottom: '1px dashed #ccc' }}>
          <span>产品名称</span><span style={{ fontWeight: 'bold' }}>{productName}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', borderBottom: '1px dashed #ccc' }}>
          <span>批次号</span><span style={{ fontFamily: 'monospace', letterSpacing: 1 }}>{batchNo}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', borderBottom: '1px dashed #ccc' }}>
          <span>净重</span><span style={{ fontWeight: 'bold', fontSize: 13 }}>{qty} {unit}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', borderBottom: '1px dashed #ccc' }}>
          <span>毛重</span><span>{qty > 0 ? (qty * 1.02).toFixed(2) : qty} {unit}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', borderBottom: '1px dashed #ccc' }}>
          <span>仓库</span><span>{warehouse}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', borderBottom: '1px dashed #ccc' }}>
          <span>生产日期</span><span>{formatDate(produceDate)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', borderBottom: '1px dashed #ccc' }}>
          <span>保质期</span><span>{shelfLifeDays}天</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', borderBottom: '1px dashed #ccc' }}>
          <span>到期日期</span><span style={{ color: '#c00', fontWeight: 'bold' }}>{formatDate(expireDate)}</span>
        </div>
      </div>
      <div style={{ marginTop: 8, borderTop: '1px solid #000', paddingTop: 4, textAlign: 'center' }}>
        <div style={{ fontFamily: 'monospace', letterSpacing: 3, fontSize: 13, background: '#fff', border: '1px solid #000', padding: '2px 4px' }}>
          {batchNo}
        </div>
        <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>批次条码</div>
      </div>
    </div>
  )
}

/** 销售配货单打印组件 */
interface OrderPrintProps {
  order: {
    order_no?: string
    created_at?: string
    delivery_date?: string
    total_amount?: string | number
    discount_amount?: string | number
    final_amount?: string | number
    remark?: string
    user_type?: number
    deliveryAddress?: { contact_name?: string; contact_phone?: string; address?: string }
    enterpriseClient?: { company_name?: string; contact_name?: string; contact_phone?: string }
    items?: Array<{ product_name?: string; quantity?: string | number; unit_price?: string | number; total_price?: string | number; unit?: string; product?: { sale_unit?: string; [key: string]: any } }>
  }
  /** 打印标题 */
  title?: string
}

export function OrderPrint({ order, title = '配货单' }: OrderPrintProps) {
  const now = new Date()
  const formatDate = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`

  const items = order.items || []
  const total = parseFloat(String(order.total_amount || 0))
  const discount = parseFloat(String(order.discount_amount || 0))
  const final = parseFloat(String(order.final_amount || 0))

  return (
    <div style={{ width: 190, padding: 15, fontFamily: 'Microsoft YaHei, SimHei', fontSize: 13 }}>
      <div style={{ textAlign: 'center', fontSize: 20, fontWeight: 'bold', marginBottom: 12 }}>{title}</div>

      {/* 企业客户名称 */}
      {order.user_type === 2 && order.enterpriseClient?.company_name && (
        <div style={{ fontSize: 14, fontWeight: 'bold', color: '#55266c', marginBottom: 8, textAlign: 'center' }}>
          🏢 {order.enterpriseClient.company_name}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 20px', fontSize: 12, marginBottom: 10, borderBottom: '1px solid #000', paddingBottom: 8 }}>
        <div>单号：{order.order_no}</div>
        <div>日期：{formatDate(order.created_at ? new Date(order.created_at) : now)}</div>
        <div>配送日：{order.delivery_date}</div>
      </div>

      {order.deliveryAddress && (
        <div style={{ fontSize: 12, marginBottom: 10 }}>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>收货信息</div>
          <div>{order.deliveryAddress.contact_name} {order.deliveryAddress.contact_phone}</div>
          <div>{order.deliveryAddress.address}</div>
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 10 }}>
        <thead>
          <tr style={{ background: '#f0f0f0' }}>
            <th style={thStyle}>商品</th>
            <th style={{ ...thStyle, width: 50 }}>数量</th>
            <th style={{ ...thStyle, width: 50 }}>单位</th>
            <th style={{ ...thStyle, width: 60 }}>单价</th>
            <th style={{ ...thStyle, width: 70 }}>金额</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={i}>
              <td style={tdStyle}>{item.product_name}</td>
              <td style={{ ...tdStyle, textAlign: 'right' }}>{parseFloat(String(item.quantity || 0))}</td>
              <td style={{ ...tdStyle, textAlign: 'center' }}>{item.product?.sale_unit || item.unit || '-'}</td>
              <td style={{ ...tdStyle, textAlign: 'right' }}>¥{parseFloat(String(item.unit_price || 0)).toFixed(2)}</td>
              <td style={{ ...tdStyle, textAlign: 'right' }}>¥{parseFloat(String(item.total_price || 0)).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ textAlign: 'right', fontSize: 14, fontWeight: 'bold', marginBottom: 8 }}>
        合计：¥{total.toFixed(2)}
        {discount > 0 && <span style={{ color: '#c00', marginLeft: 12 }}>优惠：-¥{discount.toFixed(2)}</span>}
      </div>
      <div style={{ textAlign: 'right', fontSize: 16, fontWeight: 'bold' }}>
        实付：¥{final.toFixed(2)}
      </div>

      {order.remark && (
        <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>备注：{order.remark}</div>
      )}

      <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
        <div>客户签名：________________</div>
        <div>仓库签名：________________</div>
      </div>
      <div style={{ marginTop: 16, textAlign: 'center', fontSize: 11, color: '#999' }}>
        智厨云生鲜 · 品质保障 · {formatDate(now)}
      </div>

      {/* 二维码区域 */}
      <div style={{ marginTop: 20, textAlign: 'center' }}>
        <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 8 }}>扫码确认收货</div>
        <div style={{ display: 'inline-block', padding: 8, border: '1px solid #000' }}>
          <div style={{ fontFamily: 'monospace', fontSize: 10, letterSpacing: 1 }}>
            Order:{order.order_no}
          </div>
          <div style={{ fontSize: 10, marginTop: 4, color: '#666' }}>请使用智厨云小程序扫码</div>
        </div>
        <div style={{ fontSize: 10, marginTop: 8, color: '#999' }}>
          配送员扫码确认送达 | 客户扫码确认收货
        </div>
      </div>
    </div>
  )
}

const thStyle: React.CSSProperties = { border: '1px solid #000', padding: '4px 6px', textAlign: 'left' as const, fontWeight: 'bold' as const }
const tdStyle: React.CSSProperties = { border: '1px solid #000', padding: '4px 6px' }
